import { Box, Center, Flex } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
// eslint-disable-next-line no-restricted-imports
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { ChangeEvent, useEffect, useState } from 'react';
import * as Yup from 'yup';

import { NoFeeGuarantee } from '../../../../assets/Images';
import ColorPicker from '../../../../components/ColorPicker';
import { useLEAccordionItemContext } from '../../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import NumberInput from '../../../../components/formComponents/NumberInput';
import PrimaryButton from '../../../../components/ui/buttons/PrimaryButton';
import { COUNTRY_CONFIG, getTempInfoCountry } from '../../../../constants/countries';
import { VEHICLE_COLORS } from '../../../../constants/vehicleColors';
import { useTemporaryInfoUpdateMutation } from '../../../../gql/generated/graphql';
import useImageLoader from '../../../../hooks/useImageLoader';
import { useTempInfo } from '../../../../hooks/useTempInfo';
import { overMileageWarningApplicable } from '../../../../utils/mileageWarnings';
import { odometerValidation } from '../../../../utils/validation/mileage';
import OverMileageFeeWarning from './components/OverMileageFeeWarning';

interface FormikValues {
  mileage: string;
  color: VEHICLE_COLORS;
}

const Content = ({ refreshToggle }: { refreshToggle: () => void }) => {
  const { updateState, index, setOpenAccordionIndex, isExpanded } = useLEAccordionItemContext();
  const { info } = useTempInfo({ fetchPolicy: 'network-only', refresh: isExpanded });
  const ldClient = useLDClient();
  const country = getTempInfoCountry(info?.data);
  const { odometer } = COUNTRY_CONFIG[country];

  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();

  const [showingErrors, setShowingErrors] = useState<boolean>(false);
  const [inputMileage, setInputMileage] = useState<number>(0);
  const [overMileage, setOverMileage] = useState(false);

  useImageLoader([NoFeeGuarantee]);

  const validateMileage = () => {
    if (overMileage && !showingErrors) {
      setShowingErrors(true);
      updateState({ status: 'warn' });
    }
  };

  const handleNext = async (values: FormikValues) => {
    if (!info?.data) {
      return;
    }
    updateState({ status: 'loading' });

    refreshToggle();

    const data = {
      mileage: Number(values.mileage),
      color: values.color,
    };

    await updateTemporaryInfo({
      variables: {
        info: {
          id: info.id,
          data,
        },
      },
    });

    updateState({ status: 'complete' });
    ldClient?.track('vehicle-options-selected', {});

    setOpenAccordionIndex(index + 1);
  };

  useEffect(() => {
    if (!inputMileage) return;

    setOverMileage(overMileageWarningApplicable(inputMileage, country));
  }, [inputMileage, country]);

  return (
    <Formik
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={handleNext}
      initialValues={{
        mileage: String(info?.data.mileage || ''),
        color: (info?.data.color || '') as VEHICLE_COLORS,
      }}
      validationSchema={Yup.object({
        mileage: odometerValidation(country),
        color: Yup.string().required('Color is required'),
      })}
    >
      {({ isSubmitting, values }) => {
        const mileage = Number(values.mileage);

        return (
          <Form>
            <Flex
              gap="10px"
              flexDir={{ base: 'column', md: 'row' }}
              mt={{ md: '45px' }}
              mb={showingErrors ? 5 : 0}
            >
              <Box w="full" h="fit-content">
                <NumberInput
                  name="mileage"
                  placeholder={odometer.placeholder}
                  label="Current Odometer"
                  showThousandSeparator
                  isWhole
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setInputMileage(Number(event.target.value.replace(',', '')));
                    setShowingErrors(false);
                    updateState({ status: 'none' });
                  }}
                  onBlur={validateMileage}
                />
              </Box>
              <ColorPicker />
            </Flex>
            {showingErrors && <OverMileageFeeWarning mileage={Number(mileage)} country={country} />}
            <Center mt="30px">
              <PrimaryButton type="submit" isLoading={isSubmitting}>
                NEXT
              </PrimaryButton>
            </Center>
          </Form>
        );
      }}
    </Formik>
  );
};

export default Content;
